/* sugar:assets/section-text-fill.js@2.1.8 */
/*
  Section Animation: Fill text as you scroll.

  The Section Animation block prints a marker (data-text-fill="<dimmed opacity>") inside its
  section. Every Heading and Text in that section starts dimmed, and each line paints in to full
  colour, left to right, as it crosses the fill line just below the middle of the screen (55% down),
  tied to the scroll position, so a line at mid-screen is about two-thirds filled.

  - The words are wrapped in plain inline spans once, inside the text's own markup, so bold,
    italic, links and highlight children keep their elements and styles. Inline icons and the
    date / location / countdown slots move as one piece. Text painted with a gradient (clipped
    to the letters) is not split; the gradient element fills as one piece.
  - Lines are found by measuring the words at runtime, so a phone's own line breaks just work.
    Positions are layout offsets relative to the section (transforms ignored), measured again
    on resize, when the section changes size and when web fonts arrive.
  - Each frame reads every near section's position first, then writes. A word is written only
    when its state changes: a dimmed word takes the opacity, a word the fill edge is crossing
    takes a two-stop mask, a filled word has neither. Nothing runs for a section that is not
    near the screen.
  - The theme editor and reduced motion leave the text filled: the words are never wrapped.
*/
(function () {
  'use strict';

  if (window.SugarTextFill) {
    window.SugarTextFill.scan();
    return;
  }

  var HOSTS = '.sugar-heading__content, .sugar-text__content, .sugar-heading-text__heading, .sugar-heading-text__text';
  var ATOMS = '.text-block__inline-icon, [data-location-slot], [data-date-slot], [data-countdown-slot], [data-rotate], img, svg, sugar-animated-icon, .sugar-icon';
  var SKIP = 'script, style, template, noscript';
  /* A word splits after a hyphen or dash, where the browser may wrap it */
  var PIECES = /[^\-–—]*[\-–—]|[^\-–—]+/g;

  var instances = [];
  var ticking = false;
  var resizeTimer = null;

  var reduce = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  function still() {
    return !!((window.Shopify && window.Shopify.designMode) || (reduce && reduce.matches));
  }

  /* ---------- Wrapping (writes, once per section) ---------- */

  function clipsText(el) {
    var cs = getComputedStyle(el);
    return cs.backgroundClip === 'text' || cs.webkitBackgroundClip === 'text';
  }

  /* Text nodes and atoms in reading order. Reads only. */
  function collect(node, out) {
    for (var child = node.firstChild; child; child = child.nextSibling) {
      if (child.nodeType === 3) {
        if (/\S/.test(child.nodeValue)) out.push(child);
      } else if (child.nodeType === 1) {
        if (child.matches(SKIP)) continue;
        if (child.matches(ATOMS) || clipsText(child)) {
          out.push(child);
          continue;
        }
        collect(child, out);
      }
    }
  }

  /* Spaces stay plain text between the words: a space inside a word's span ends that span's
     underline early, which would break a link's underline even once the line is filled */
  function wrapText(textNode, units) {
    var parts = textNode.nodeValue.split(/(\s+)/);
    var frag = document.createDocumentFragment();
    for (var i = 0; i < parts.length; i++) {
      var part = parts[i];
      if (!part) continue;
      if (/^\s+$/.test(part)) {
        frag.appendChild(document.createTextNode(part));
        continue;
      }
      var pieces = part.match(PIECES) || [part];
      for (var j = 0; j < pieces.length; j++) {
        var span = document.createElement('span');
        span.setAttribute('data-tf-w', '');
        span.textContent = pieces[j];
        frag.appendChild(span);
        units.push({ el: span });
      }
    }
    textNode.parentNode.replaceChild(frag, textNode);
  }

  function create(marker) {
    var root = marker.closest('.shopify-section') || marker.parentElement;
    if (!root || root.__sugarTextFill) return null;

    var dim = parseFloat(marker.getAttribute('data-text-fill'));
    if (!isFinite(dim)) dim = 0.5;
    dim = Math.min(1, Math.max(0, dim));

    var hosts = Array.prototype.slice.call(root.querySelectorAll(HOSTS));
    /* A host inside another is wrapped with its outer host */
    hosts = hosts.filter(function (host) {
      return !hosts.some(function (other) {
        return other !== host && other.contains(host);
      });
    });
    var inst = { root: root, dim: String(dim), dimNum: dim, hosts: [], lines: [], near: false };
    root.__sugarTextFill = inst;

    /* Reads for every host first */
    hosts.forEach(function (host) {
      if (host.closest('[data-tf-host]')) return;
      var items = [];
      if (clipsText(host)) items.push(host);
      else collect(host, items);
      inst.hosts.push({ el: host, items: items, units: [], rtl: getComputedStyle(host).direction === 'rtl' });
    });
    return inst;
  }

  function wrap(inst) {
    inst.hosts.forEach(function (h) {
      h.el.setAttribute('data-tf-host', '');
      h.items.forEach(function (item) {
        if (item.nodeType === 3) wrapText(item, h.units);
        else h.units.push({ el: item });
      });
      h.items = null;
    });
  }

  /* ---------- Measuring (reads) ---------- */

  function offsetOf(el, cache) {
    var x = 0;
    var y = 0;
    var node = el;
    var chain = [];
    while (node) {
      var hit = cache.get(node);
      if (hit) {
        x += hit[0];
        y += hit[1];
        break;
      }
      chain.push(node);
      x += node.offsetLeft;
      y += node.offsetTop;
      node = node.offsetParent;
    }
    /* Remember each ancestor's own document offset for the next word */
    var cx = x;
    var cy = y;
    for (var i = 0; i < chain.length; i++) {
      var n = chain[i];
      if (i > 0) cache.set(n, [cx, cy]);
      cx -= n.offsetLeft;
      cy -= n.offsetTop;
    }
    return [x, y];
  }

  function measure(inst) {
    var cache = new Map();
    var origin = offsetOf(inst.root, cache);
    var lines = [];
    inst.hosts.forEach(function (h) {
      var line = null;
      h.units.forEach(function (u) {
        var w = u.el.offsetWidth;
        var ht = u.el.offsetHeight;
        if (!w && !ht) return;
        var off = offsetOf(u.el, cache);
        var x = off[0] - origin[0];
        var y = off[1] - origin[1];
        if (!line || y > line.y + line.h * 0.5) {
          line = { y: y, h: ht, x0: x, x1: x + w, rtl: h.rtl, units: [] };
          lines.push(line);
        } else {
          if (ht > line.h) line.h = ht;
          if (x < line.x0) line.x0 = x;
          if (x + w > line.x1) line.x1 = x + w;
        }
        u.x = x;
        u.w = Math.max(1, w);
        line.units.push(u);
      });
    });
    inst.lines = lines;
  }

  /* ---------- Painting (writes) ---------- */

  function paint(u, q, inst, rtl) {
    if (u.q === q) return;
    var s = u.el.style;
    var wasPartial = u.q !== undefined && u.q > 0 && u.q < 1;
    if (q === 1) {
      if (u.q === 0) s.opacity = '';
      if (wasPartial) s.webkitMaskImage = s.maskImage = '';
    } else if (q === 0) {
      s.opacity = inst.dim;
      if (wasPartial) s.webkitMaskImage = s.maskImage = '';
    } else {
      if (u.q === 0) s.opacity = '';
      var pct = (q * 100).toFixed(1) + '%';
      var g = 'linear-gradient(' + (rtl ? 'to left' : 'to right') + ',#000 ' + pct + ',rgba(0,0,0,' + inst.dim + ') ' + pct + ')';
      s.webkitMaskImage = g;
      s.maskImage = g;
    }
    u.q = q;
  }

  function step(v) {
    if (v <= 0) return 0;
    if (v >= 1) return 1;
    return Math.round(v * 400) / 400;
  }

  /* top: the section's top on screen; mid: the fill line, where a line's top starts to fill */
  function render(inst, top, mid) {
    for (var i = 0; i < inst.lines.length; i++) {
      var line = inst.lines[i];
      var centre = top + line.y + line.h / 2;
      var p = (mid - centre) / Math.max(line.h, 1) + 0.5;
      p = p <= 0 ? 0 : p >= 1 ? 1 : p;
      var span = line.x1 - line.x0;
      var edge = line.rtl ? line.x1 - p * span : line.x0 + p * span;
      for (var j = 0; j < line.units.length; j++) {
        var u = line.units[j];
        var q;
        if (p === 0) q = 0;
        else if (p === 1) q = 1;
        else if (line.rtl) q = step((u.x + u.w - edge) / u.w);
        else q = step((edge - u.x) / u.w);
        paint(u, q, inst, line.rtl);
      }
    }
  }

  var FILL_LINE = 0.55;

  function update(list) {
    var mid = (window.innerHeight || document.documentElement.clientHeight) * FILL_LINE;
    var tops = [];
    var i;
    for (i = 0; i < list.length; i++) tops.push(list[i].root.getBoundingClientRect().top);
    for (i = 0; i < list.length; i++) render(list[i], tops[i], mid);
  }

  function nearOnes() {
    return instances.filter(function (inst) {
      return inst.near;
    });
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      ticking = false;
      update(nearOnes());
    });
  }

  function remeasure(list) {
    list.forEach(measure);
    update(list);
  }

  var lastWidth = window.innerWidth;

  /* A height-only resize (a phone's address bar) moves the middle line, not the lines */
  function onResize() {
    if (window.innerWidth === lastWidth) {
      onScroll();
      return;
    }
    lastWidth = window.innerWidth;
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      remeasure(instances);
    }, 150);
  }

  /* ---------- Watching ---------- */

  var io = null;
  var ro = null;
  var roPending = [];

  function watch(inst) {
    if (!io) {
      io = new IntersectionObserver(
        function (entries) {
          var changed = [];
          entries.forEach(function (entry) {
            var it = entry.target.__sugarTextFill;
            if (!it) return;
            it.near = entry.isIntersecting;
            changed.push(it);
          });
          /* One last paint for a section that just left, so a jump past it leaves it settled */
          if (changed.length) update(changed);
        },
        { rootMargin: '50% 0px 50% 0px' }
      );
    }
    io.observe(inst.root);

    if ('ResizeObserver' in window) {
      if (!ro) {
        ro = new ResizeObserver(function (entries) {
          entries.forEach(function (entry) {
            var it = entry.target.__sugarTextFill;
            if (!it) return;
            /* The first report is the size the section already had */
            if (!it.roSeen) {
              it.roSeen = true;
              return;
            }
            if (roPending.indexOf(it) === -1) roPending.push(it);
          });
          if (!roPending.length) return;
          requestAnimationFrame(function () {
            var list = roPending;
            roPending = [];
            remeasure(list);
          });
        });
      }
      ro.observe(inst.root);
    }
  }

  function scan() {
    if (still()) return;
    var created = [];
    document.querySelectorAll('[data-text-fill]').forEach(function (marker) {
      var inst = create(marker);
      if (inst) created.push(inst);
    });
    if (!created.length) return;

    /* Reads above, writes here, then one measure for all, then paint */
    created.forEach(wrap);
    created.forEach(measure);
    update(created);

    created.forEach(function (inst) {
      instances.push(inst);
      watch(inst);
    });
  }

  window.SugarTextFill = { scan: scan };

  if (still()) return;

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onResize, { passive: true });
  if (document.fonts) {
    if (document.fonts.ready) document.fonts.ready.then(function () { remeasure(instances); });
    if (document.fonts.addEventListener) {
      document.fonts.addEventListener('loadingdone', function () {
        remeasure(instances);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan, { once: true });
  } else {
    scan();
  }
})();
